/*
 * Scheduled themes (CLAUDE.md §4, Saif 20 Oct 2026): a named look for a special or a season, from a start to an end,
 * that applies by itself and switches back. Only one runs at a time, so two scheduled themes may never overlap; the
 * everyday theme shows whenever none is running.
 */

export interface Scheduled { id: string; startsAt: string; endsAt: string }

export type ScheduleStatus = 'running' | 'upcoming' | 'ended';

export function scheduleStatus(s: Pick<Scheduled, 'startsAt' | 'endsAt'>, now: Date): ScheduleStatus {
  const t = now.toISOString();
  if (s.endsAt <= t) return 'ended';
  return s.startsAt <= t ? 'running' : 'upcoming';
}

/** The scheduled theme running now, if any. */
export function runningNow<T extends Scheduled>(list: readonly T[], now: Date): T | null {
  return list.find((s) => scheduleStatus(s, now) === 'running') ?? null;
}

/** The next one to start after now, if any. */
export function nextUp<T extends Scheduled>(list: readonly T[], now: Date): T | null {
  const t = now.toISOString();
  return [...list].filter((s) => s.startsAt > t).sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0] ?? null;
}

/**
 * When the look next changes by itself: the running theme's end, or the next one's start. The app keeps what it has
 * until then (or a minute, whichever is sooner), so a special starts and ends on time without a new app version.
 */
export function nextChangeAt(list: readonly Scheduled[], now: Date): string | null {
  const running = runningNow(list, now);
  const next = nextUp(list, now);
  if (running && next) return running.endsAt < next.startsAt ? running.endsAt : next.startsAt;
  return running?.endsAt ?? next?.startsAt ?? null;
}

/** Another scheduled theme whose dates cross these (ended ones never count), or null. */
export function overlapping<T extends Scheduled>(list: readonly T[], s: Pick<Scheduled, 'id' | 'startsAt' | 'endsAt'>, now: Date): T | null {
  return list.find((o) => o.id !== s.id && scheduleStatus(o, now) !== 'ended' && o.startsAt < s.endsAt && s.startsAt < o.endsAt) ?? null;
}
